/** SUNTRONIX ONE — ONE GROUP establishment switcher (ONE bar).
 *  Shows only establishments the user may access, plus "All Establishments" for
 *  authorised roles. The choice is a context hint; access is enforced server-side.
 */
import { useEffect, useState } from "react";
import { Building2, ChevronDown, Check, Globe2 } from "lucide-react";
import api from "@/lib/api";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

const KEY = "st.establishment";

/** Display name of the establishment selected in the switcher ("ONE GROUP" for All); null until loaded. */
let ctxP = null;
const loadCtx = () => (ctxP ||= api.get("/establishments/context"));
export function useCurrentEstablishmentName() {
  const [n, setN] = useState(null);
  useEffect(() => {
    loadCtx().then(({ data }) => {
      let sel = ""; try { sel = window.localStorage.getItem(KEY) || ""; } catch { /* ignore */ }
      if (sel === "ALL") return setN("ONE GROUP");
      const cur = (data?.establishments || []).find((e) => e.id === (sel || data.selected));
      setN(cur?.display_name || "SUNTRONIX");
    }).catch(() => { ctxP = null; setN("SUNTRONIX"); });
  }, []);
  return n;
}

export default function EstablishmentSwitcher({ testid = "establishment-switcher", optPrefix = "establishment-opt" }) {
  const [data, setData] = useState(null);
  const [sel, setSel] = useState(() => { try { return window.localStorage.getItem(KEY) || ""; } catch { return ""; } });

  useEffect(() => { api.get("/establishments/context").then(({ data }) => setData(data)).catch(() => setData(null)); }, []);

  const choose = (id) => {
    try { id ? window.localStorage.setItem(KEY, id) : window.localStorage.removeItem(KEY); } catch { /* ignore */ }
    setSel(id || "");
    const u = new URL(window.location.href);   // a selected record belongs to the previous company → drop it
    u.searchParams.delete("open");
    window.location.replace(u.toString());   // re-fetch all context with the new establishment
  };

  if (!data || !data.establishments?.length) return null;
  const current = data.establishments.find((e) => e.id === (sel || data.selected));
  const label = sel === "ALL" ? "All Establishments" : (current?.display_name || data.establishments[0]?.display_name || "SUNTRONIX");

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button data-testid={testid}
          className="inline-flex items-center gap-1.5 h-8 px-2.5 rounded-lg border border-[color:var(--st-border)] text-[13px] font-medium text-[color:var(--st-text)] hover:bg-[color:var(--st-bg)] transition-colors">
          <Building2 className="h-3.5 w-3.5 text-[color:var(--st-accent-space,var(--st-accent))]" />
          <span className="max-w-[140px] truncate">{label}</span>
          <ChevronDown className="h-3.5 w-3.5 opacity-60" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-56">
        {data.establishments.map((e) => (
          <DropdownMenuItem key={e.id} onSelect={() => choose(e.id)} data-testid={`${optPrefix}-${e.id}`}>
            <Building2 className="h-4 w-4 mr-2 opacity-70" />
            <span className="flex-1 truncate">{e.display_name}</span>
            {e.status === "TO_BE_CONFIGURED" && <span className="text-[9px] text-slate-400 mr-1">setup</span>}
            {(sel || data.selected) === e.id && sel !== "ALL" && <Check className="h-3.5 w-3.5" />}
          </DropdownMenuItem>
        ))}
        {data.can_all && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={() => choose("ALL")} data-testid={`${optPrefix}-all`}>
              <Globe2 className="h-4 w-4 mr-2 opacity-70" />
              <span className="flex-1">All Establishments</span>
              {sel === "ALL" && <Check className="h-3.5 w-3.5" />}
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
